/**
 * Types, interfaces, and enums extracted from the former GraphQL operations.
 * These are pure TypeScript types used across the application.
 */

export type Maybe<T> = T | null;
export type InputMaybe<T> = Maybe<T>;

/** Information about a dealer location. */
export type DealerLocation = {
  __typename?: 'DealerLocation';
  /** Primary address line of the dealer location */
  addressLine1: string;
  /** Secondary address line of the dealer location */
  addressLine2?: Maybe<string>;
  /** City where the dealer is located */
  city: string;
  /** Unique identifier for the dealer */
  dealerId: string;
  /** Name of the dealer */
  dealerName: string;
  /** Distance to the dealer location */
  distance: string;
  /** Latitude coordinate of the dealer location */
  latitude: string;
  /** Longitude coordinate of the dealer location */
  longitude: string;
  /** Phone number of the dealer */
  phone?: string;
  /** Score of the dealer location */
  score: string;
  /** State where the dealer is located */
  state: string;
  /** Zip code of the dealer location */
  zip: string;
  /** website of the dealer location */
  website?: Maybe<string>;
  /** Dealer selected from URL */
  isDealerSelected?: Maybe<boolean>;
  inventoryUrl?: string | undefined;
};

/** Customer residence info. */
export type ResidenceInfo = {
  __typename?: 'ResidenceInfo';
  /** Primary address line of the residence */
  addressLine1: string;
  /** Secondary address line of the residence */
  addressLine2?: Maybe<string>;
  /** City of the residence */
  city: string;
  /** month/year started residing at the residence */
  monthResiding: string;
  /** Ownership status of the residence */
  ownedBy: string;
  /** Unique identifier for the personal information */
  personalInfoId: number;
  /** Unique identifier for the residence information */
  residenceInfoId: number;
  /** State of the residence */
  userState: string;
  /** Zip code of the residence */
  zipCode: string;
};

/** The different zip code verification statuses */
export enum ZipCodeStatus {
  InvalidTerritoryError = 'invalidTerritoryError',
  ValidZipCode = 'validZipCode',
  ZipCodeError = 'zipCodeError',
}

/** Input model for the account details of the user */
export type AccountInput = {
  /** List of bank account types for the user */
  bankingType: string[];
};

/** Input model for the application details */
export type ApplicationInput = {
  /** Type of flow for the application process */
  flowtype: string;
  /** Source from which the application was submitted */
  source: string;
};

/** Input model for the income details of the user */
export type IncomeInput = {
  /** Bi-weekly pay of the user */
  biWeeklyPay: string;
  /** Name of the employer */
  employerName?: InputMaybe<string>;
  /** Phone number of the employer */
  employerPhone?: InputMaybe<string>;
  /** Order of employments for the user */
  employmentOrder: number;
  /** Hire date of the user */
  hireDate: string;
  /** Type of income */
  incomeType: string;
};

/** Input model for the personal information of the user */
export type PersonalInfoInput = {
  /** Application details associated with the personal information */
  application: ApplicationInput;
  /** Date of birth of the user */
  dateOfBirth: string;
  /** Currently not being used */
  driver?: InputMaybe<number>;
  /** Email address of the user */
  email: string;
  /** First name of the user */
  firstName: string;
  /** Last name of the user */
  lastName: string;
  /** Phone number of the user */
  phone: string;
};

/** Input model for the residence information of the user */
export type ResidenceInfoInput = {
  /** Primary address line of the residence */
  addressLine1: string;
  /** Secondary address line of the residence */
  addressLine2?: InputMaybe<string>;
  /** City of the residence */
  city: string;
  /** month/year started residing at the residence */
  monthResiding: string;
  /** Ownership status of the residence */
  ownedBy: string;
  /** State of the residence */
  userState: string;
  /** Zip code of the residence */
  zipCode: string;
};

/** Input model for the long form application process */
export type LongFormInfoInput = {
  /** Account information of the user */
  account: AccountInput;
  /** Indicates if the user has given consent for credit authorization */
  creditauthConsent: boolean;
  /** List of income details of the user */
  income: IncomeInput[];
  /** Personal information of the user */
  personal: PersonalInfoInput;
  /** Indicates if the user has given consent for phone and email communication */
  phoneEmailConsent: boolean;
  /** Residence information of the user */
  residence: ResidenceInfoInput;
  /** Social Security Number of the user */
  ssn: string;
  /** Vehicle Type of the user */
  vehicleType?: InputMaybe<string[]>;
  /** Vehicle Make of the user */
  vehicleMake?: InputMaybe<string[]>;
};

/** Bureau-derived risk flags for the primary applicant */
export type BureauFlags = {
  __typename?: 'BureauFlags';
  /** Indicates the bureau returned a fraud alert on the applicant */
  fraudFlag: boolean;
  /** Indicates the bureau flagged the applicant's SSN as deceased */
  deceasedSsnFlag: boolean;
  /** Indicates no credit bureau record was found for the applicant */
  noCreditBureauFlag: boolean;
  /** Bureau(s) the flags above were derived from */
  flagsDerivedFrom: string[];
};

/** Primary applicant details returned with the prequalification response */
export type PrimaryApplicant = {
  __typename?: 'PrimaryApplicant';
  bureauFlags: BureauFlags;
};

/** Response model for the long form prequalification process */
export type LongFormPrequalResponse = {
  __typename?: 'LongFormPrequalResponse';
  /** List of dealer locations associated with the application */
  dealerLocations?: Maybe<DealerLocation[]>;
  /** Reason for failure if the prequalification process fails */
  failureReason: string;
  /** Indicates if the user has a frozen credit bureau */
  frozenBureau: boolean;
  /** Indicates if the user has provided an invalid Social Security Number */
  invalidSsn: boolean;
  /** Primary applicant details, including bureau-derived risk flags */
  primaryApplicant?: Maybe<PrimaryApplicant>;
  /** Maximum finance amount allowed */
  maxFinanceAmount?: Maybe<number>;
  /** Maximum monthly payment allowed */
  maxMonthlyPayment?: Maybe<number>;
  /** Minimum down payment required */
  minimumDownPayment?: Maybe<number>;
  /** Indicates if the user has an open loan */
  openLoan: boolean;
  /** Unique identifier for the portal application */
  portalApplicationId?: Maybe<number>;
  /** Status of the prequalification process */
  status: string;
  /** CAPS field-validation failures mapped onto FE controls */
  errorType?: 'validation';
  fieldErrors?: {
    field: string;
    code: string;
    message: string;
  }[];
};
